/**
 * GateManagement Service
 * 게이트 관리 비즈니스 로직
 */
import { GateType, GateStatus, GateDirection } from '../models/GateManagement';

export class GateManagementService {
  /**
   * 게이트 타입 라벨 조회
   */
  getGateTypeLabel(type: GateType): string {
    const labels: Record<GateType, string> = {
      main_entrance: '메인 출입구',
      study_room: '열람실',
      restroom: '화장실',
      lounge: '휴게실',
    };
    return labels[type];
  }

  /**
   * 게이트 타입 색상 조회
   */
  getGateTypeColor(type: GateType): string {
    const colors: Record<GateType, string> = {
      main_entrance: 'bg-blue-100 text-blue-800',
      study_room: 'bg-green-100 text-green-800',
      restroom: 'bg-yellow-100 text-yellow-800',
      lounge: 'bg-purple-100 text-purple-800',
    };
    return colors[type];
  }

  /**
   * 게이트 상태 라벨 조회
   */
  getGateStatusLabel(status: GateStatus): string {
    return status === 'online' ? '온라인' : '오프라인';
  }

  /**
   * 게이트 상태 색상 조회
   */
  getGateStatusColor(status: GateStatus): string {
    return status === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }

  /**
   * 게이트 방향 라벨 조회
   */
  getGateDirectionLabel(direction: GateDirection): string {
    const labels: Record<GateDirection, string> = {
      entry: '📥 입구',
      exit: '📤 출구',
      both: '🔄 입출구',
    };
    return labels[direction];
  }

  /**
   * 게이트 방향 색상 조회
   */
  getGateDirectionColor(direction: GateDirection): string {
    const colors: Record<GateDirection, string> = {
      entry: 'bg-green-100 text-green-800',
      exit: 'bg-orange-100 text-orange-800',
      both: 'bg-blue-100 text-blue-800',
    };
    return colors[direction];
  }

  /**
   * 마지막 하트비트 시간 포맷팅
   */
  formatLastHeartbeat(lastHeartbeat?: string): string {
    if (!lastHeartbeat) return '-';

    const date = new Date(lastHeartbeat);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 1000 / 60);

    if (minutes < 1) return '방금 전';
    if (minutes < 60) return `${minutes}분 전`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}시간 전`;

    const days = Math.floor(hours / 24);
    return `${days}일 전`;
  }

  /**
   * QR 코드 URL 생성
   */
  generateQRCodeURL(gateId: string, token: string): string {
    const baseURL = window.location.origin;
    return `${baseURL}/gate-connect?id=${gateId}&token=${token}`;
  }

  /**
   * 게이트 폼 검증
   */
  validateGateForm(name: string, location: string): { isValid: boolean; error?: string } {
    if (!name.trim()) {
      return { isValid: false, error: '게이트 이름을 입력해주세요.' };
    }

    if (!location.trim()) {
      return { isValid: false, error: '게이트 위치를 입력해주세요.' };
    }

    if (name.length > 50) {
      return { isValid: false, error: '게이트 이름은 50자 이내로 입력해주세요.' };
    }

    if (location.length > 100) {
      return { isValid: false, error: '게이트 위치는 100자 이내로 입력해주세요.' };
    }

    return { isValid: true };
  }
}

export const gateManagementService = new GateManagementService();
